import { takeEvery, call, put } from 'redux-saga/effects';
import { gcApi } from 'config/axios';
import moment from 'moment';

import { removeNulls } from 'utils/ObjectUtil';

import { sortBabyMovements, sortGlucoseList } from 'utils/PregnancyUtil';

import ViewerSocialProfile from 'models/user/ViewerSocialProfile';

import { CYCLE_TYPE_PREGNANT } from 'constants/MenstrualCycleTrackingConstants';

import { handlingErrorCallback, handlingSuccessCallback } from './sagasUtils';

import {
    COMPLETE_STOP_TRACKING,
    COMPLETE_STOP_TRACKING_START,
    COMPLETE_STOP_TRACKING_SUCCESS,
    COMPLETE_STOP_TRACKING_ERROR,
    TAKE_A_BREAK,
    TAKE_A_BREAK_START,
    TAKE_A_BREAK_SUCCESS,
    TAKE_A_BREAK_ERROR,
    INITIAL_PREGNANCY_CREATE,
    INITIAL_PREGNANCY_CREATE_SUCCESS,
    INITIAL_PREGNANCY_CREATE_ERROR,
    INITIAL_PREGNANCY_CREATE_SEND,
    INITIAL_PREGNANCY_HYDRATION_ERROR,
    INITIAL_PREGNANCY_TRAINING_STATUS_ERROR,
    INITIAL_PREGNANCY_WEIGHT_GAIN_GOALS_ERROR,
    INITIAL_PREGNANCY_INDEX_SCALE_ERROR,
    INITIAL_PREGNANCY_MOVE_ALERT_ERROR,
    FETCH_PREGNANCY_SNAPSHOTS,
    FETCH_PREGNANCY_SNAPSHOTS_FAIL,
    FETCH_PREGNANCY_SNAPSHOTS_FETCHING,
    FETCH_PREGNANCY_SNAPSHOTS_SUCCESS,
    UPDATE_PREGNANCY_SNAPSHOT,
    UPDATE_PREGNANCY_SNAPSHOT_UPDATING,
    UPDATE_PREGNANCY_SNAPSHOT_SUCCESS,
    UPDATE_PREGNANCY_SNAPSHOT_FAIL,
    DELETE_PREGNANCY_SNAPSHOT,
    FETCH_PREGNANCY_DAYVIEW,
    FETCH_PREGNANCY_DAYVIEW_FETCHING,
    FETCH_PREGNANCY_DAYVIEW_SUCCESS,
    FETCH_PREGNANCY_DAYVIEW_FAIL,
    UPDATE_PREGNANCY_DAILY_LOG,
    UPDATE_PREGNANCY_DAILY_LOG_SUCCESS,
    UPDATE_PREGNANCY_DAILY_LOG_FAIL,
    FETCH_PREGNANCY_GLUCOSE,
    FETCH_PREGNANCY_GLUCOSE_FETCHING,
    FETCH_PREGNANCY_GLUCOSE_SUCCESS,
    FETCH_PREGNANCY_GLUCOSE_FAIL,
    ADD_PREGNANCY_GLUCOSE,
    ADD_PREGNANCY_GLUCOSE_SUCCESS,
    ADD_PREGNANCY_GLUCOSE_FAIL,
    UPDATE_PREGNANCY_GLUCOSE,
    UPDATE_PREGNANCY_GLUCOSE_SUCCESS,
    UPDATE_PREGNANCY_GLUCOSE_FAIL,
    DELETE_PREGNANCY_GLUCOSE,
    DELETE_PREGNANCY_GLUCOSE_SUCCESS,
    DELETE_PREGNANCY_GLUCOSE_FAIL,
    FETCH_PREGNANCY_ICON_SUCCESS,
    FETCH_PREGNANCY_ICON_FAIL,
    FETCH_PREGNANCY_ICON,
    INITIAL_PREGNANCY_CREATE_CONFLICT_ERROR,
    FETCH_PREGNANCY_WEIGHT_GAIN_GOALS,
    FETCH_PREGNANCY_WEIGHT_GAIN_GOALS_FETCHING,
    FETCH_PREGNANCY_WEIGHT_GAIN_GOALS_SUCCESS,
    FETCH_PREGNANCY_WEIGHT_GAIN_GOALS_FAIL
} from 'actions/pregnancyTrackingActions';

import { StopTrackingTypes, PregnancyIcons } from 'utils/PregnancyUtil';
import DateFormatter from 'utils/date/DateFormatter';

export function* takeBreak(action) {
    try {
        let response = null,
            timeStampGmt = DateFormatter.formatConnectTimestampUTC(moment().toDate()),
            endDate = action.data.endDate ? moment(action.data.endDate).format(DateFormatter.LINK_FORMAT) : null,
            dueDate = action.data.dueDate,
            title = action.data.title,
            originalDueDate = action.data.originalDueDate;

        yield put({
            type: TAKE_A_BREAK_START
        });

        if (!dueDate) {
            // Need to get dueDate for next calls
            response = yield call(gcApi.get, '/periodichealth-service/menstrualcycle/pregnancysnapshot/');
            dueDate = response.data.dueDate;
            originalDueDate = response.data.originalDueDate;
            title = response.data.title;
        }

        response = yield call(
            gcApi.put,
            `/periodichealth-service/menstrualcycle/pregnancysnapshot/${originalDueDate}`,
            {
                dueDate: dueDate,
                stopTracking: true,
                reportTimestamp: timeStampGmt,
                title: title
            }
        );

        if (response !== null) {
            yield put({
                type: TAKE_A_BREAK_SUCCESS,
                response
            });
        }
    } catch (e) {
        yield put({
            type: TAKE_A_BREAK_ERROR
        });
    }
}

export function* stopTrackingPregnancy(action) {
    try {
        let response = null,
            timeStampGmt = DateFormatter.formatConnectTimestampUTC(moment().toDate()),
            currentDate = moment().format(DateFormatter.LINK_FORMAT),
            endDate = action.data.endDate ? moment(action.data.endDate).format(DateFormatter.LINK_FORMAT) : null,
            dueDate = action.data.dueDate,
            title = action.data.title,
            originalDueDate = action.data.originalDueDate;

        yield put({
            type: COMPLETE_STOP_TRACKING_START
        });

        if (!dueDate) {
            // Need to get dueDate for next calls
            response = yield call(gcApi.get, '/periodichealth-service/menstrualcycle/pregnancysnapshot/');
            dueDate = response.data.dueDate;
            originalDueDate = response.data.originalDueDate;
            title = response.data.title;
        }

        if (action.data.stopTrackingType === StopTrackingTypes.Had.id) {
            // "I had my baby " should result in stopTracking/experiencedLoss = false and deliverydate=<local date>
            response = yield call(
                gcApi.put,
                `/periodichealth-service/menstrualcycle/pregnancysnapshot/${originalDueDate}`,
                {
                    dueDate: dueDate,
                    experiencedLoss: false,
                    stopTracking: false,
                    notes:
                        action.data.pregnancyNotes && action.data.pregnancyNotes.trim().length > 0
                            ? action.data.pregnancyNotes
                            : null,
                    deliveryDate: endDate,
                    reportTimestamp: timeStampGmt,
                    title: title
                }
            );
        } else if (action.data.stopTrackingType === StopTrackingTypes.Loss.id) {
            // “I experienced a loss ” should result in  experiencedLoss = true, stopTracking= false
            // and deliverydate=null, endDate=local date, lossReportTimestamp = gmt time
            response = yield call(
                gcApi.put,
                `/periodichealth-service/menstrualcycle/pregnancysnapshot/${originalDueDate}`,
                {
                    dueDate: dueDate,
                    experiencedLoss: true,
                    deliveryDate: null,
                    stopTracking: false,
                    notes:
                        action.data.pregnancyNotes && action.data.pregnancyNotes.trim().length > 0
                            ? action.data.pregnancyNotes
                            : null,
                    endDate: endDate,
                    lossReportTimestamp: timeStampGmt,
                    reportTimestamp: timeStampGmt,
                    title: title
                }
            );
        } else if (action.data.stopTrackingType === StopTrackingTypes.NotSay.id) {
            // “I prefer not to say” should result in  experiencedLoss = false, stopTracking= true
            // and deliverydate=null,  endDate=local date, stopTrackingTimestamp = gmt time,
            // API to set cycle type to INACTIVE and set the inactive date in settings as result of this.
            response = yield call(
                gcApi.put,
                `/periodichealth-service/menstrualcycle/pregnancysnapshot/${originalDueDate}`,
                {
                    dueDate: dueDate,
                    experiencedLoss: false,
                    stopTracking: true,
                    deliveryDate: null,
                    endDate: currentDate,
                    stopTrackingTimestamp: timeStampGmt,
                    reportTimestamp: timeStampGmt,
                    title: title
                }
            );
        } else {
            yield put({
                type: COMPLETE_STOP_TRACKING_ERROR
            });
        }

        if (response !== null) {
            yield put({
                type: COMPLETE_STOP_TRACKING_SUCCESS,
                response
            });
        }
    } catch (e) {
        yield put({
            type: COMPLETE_STOP_TRACKING_ERROR
        });
    }
}

const DefaultScreens = [
    {
        screenId: 'APP_NATIVE_ID_BMI',
        screenIndex: 2,
        enabled: false
    },
    {
        screenId: 'APP_NATIVE_ID_BODY_FAT',
        screenIndex: 3,
        enabled: false
    },
    {
        screenId: 'APP_NATIVE_ID_MUSCLE_MASS',
        screenIndex: 4,
        enabled: false
    },
    {
        screenId: 'APP_NATIVE_ID_BONE_MASS',
        screenIndex: 5,
        enabled: false
    },
    {
        screenId: 'APP_NATIVE_ID_BODY_WATER',
        screenIndex: 6,
        enabled: false
    }
];

export function* create(action) {
    let response = null,
        timeStampGmt = DateFormatter.formatConnectTimestampUTC(moment().toDate()),
        currentDate = moment().format(DateFormatter.LINK_FORMAT),
        setupDetail = action.setupDetail,
        currentUserSettings = action.setupDetail.userSettings,
        dueDate = moment(setupDetail.dueDate).format(DateFormatter.LINK_FORMAT),
        setupFailed = false,
        messageType = INITIAL_PREGNANCY_CREATE_SUCCESS;

    yield put({
        type: INITIAL_PREGNANCY_CREATE_SEND
    });

    try {
        let mctSettings = {
            userMenstrualCycleSettings: {
                menstrualCycleType: CYCLE_TYPE_PREGNANT,
                pregnancySetup: true
            }
        };

        if (
            !currentUserSettings ||
            !currentUserSettings.userMenstrualCycleSettings ||
            typeof currentUserSettings.userMenstrualCycleSettings.babyMovementTracking === 'undefined' ||
            typeof currentUserSettings.userMenstrualCycleSettings.glucoseTracking === 'undefined'
        ) {
            mctSettings.userMenstrualCycleSettings.babyMovementTracking = true;
            mctSettings.userMenstrualCycleSettings.glucoseTracking = true;
        }

        response = yield call(gcApi.put, '/userprofile-service/userprofile/user-settings', mctSettings);

        let weightUserInputs = {
            weightGoalRecommendation: setupDetail.weightGoalUserInputs.weightGoalRecommendation
        };

        if (setupDetail.weightGoalUserInputs.weightGoalRecommendation) {
            weightUserInputs.heightInCm = setupDetail.weightGoalUserInputs.heightInCm;
            weightUserInputs.weightInGram = setupDetail.weightGoalUserInputs.weightInGram;
        }

        let pregnancySetup = {
            userProfilePk: ViewerSocialProfile.get('profileId'),
            title: setupDetail.title,
            dueDate: dueDate,
            weightGoalUserInputs: weightUserInputs,
            reportTimestamp: timeStampGmt
        };

        if (setupDetail.hydrationGoal) {
            pregnancySetup.pregnancyHealthGoals = [
                {
                    userGoalType: 'HYDRATION',
                    userGoalValue: setupDetail.hydrationGoal
                }
            ];
        }

        response = yield call(
            gcApi.post,
            `/periodichealth-service/menstrualcycle/initpregnancysetup/${currentDate}`,
            pregnancySetup,
            {
                validateStatus: (status) => {
                    return ('' + status).startsWith('409') || status === 200;
                }
            }
        );

        if (response.status === 409) {
            if (response.data.message && response.data.message.startsWith('Pregnancy')) {
                yield put({
                    type: INITIAL_PREGNANCY_CREATE_CONFLICT_ERROR,
                    conflictType: CYCLE_TYPE_PREGNANT,
                    periodStartDate: null
                });
            } else {
                // Need to know what cycle type the date conflicts with
                let lastconfirmedResp = yield call(
                    gcApi.get,
                    `/periodichealth-service/menstrualcycle/lastconfirmed/${currentDate}`
                );

                if (lastconfirmedResp && lastconfirmedResp.data) {
                    yield put({
                        type: INITIAL_PREGNANCY_CREATE_CONFLICT_ERROR,
                        conflictType: lastconfirmedResp.data.cycleType,
                        periodStartDate: lastconfirmedResp.data.periodStartDate
                    });
                } else {
                    yield put({
                        type: INITIAL_PREGNANCY_CREATE_CONFLICT_ERROR,
                        conflictType: CYCLE_TYPE_PREGNANT,
                        periodStartDate: null
                    });
                }
            }
            return;
        }

        if (response.data && response.data.setupStepErrorResponseMap) {
            if (response.data.setupStepErrorResponseMap.UPDATE_HYDRATION_GOAL) {
                setupFailed = true;
                messageType = INITIAL_PREGNANCY_HYDRATION_ERROR;
            } else if (response.data.setupStepErrorResponseMap.UPDATE_WEIGHT_GOAL) {
                setupFailed = true;
                messageType = INITIAL_PREGNANCY_WEIGHT_GAIN_GOALS_ERROR;
            }
        }

        if (!setupFailed && setupDetail.moveAlerts.deviceId) {
            try {
                response = yield call(
                    gcApi.put,
                    `/device-service/deviceservice/device-info/settings/${setupDetail.moveAlerts.deviceId}`,
                    {
                        activityTracking: {
                            moveAlertEnabled: setupDetail.moveAlerts.enabled
                        }
                    }
                );
            } catch (e) {
                messageType = INITIAL_PREGNANCY_MOVE_ALERT_ERROR;
            }
        }

        if (!setupFailed && setupDetail.advancedMetrics.deviceId) {
            let screens = [...DefaultScreens];
            let enabledScreenCount = 0;
            setupDetail.advancedMetrics.advancedMetrics.forEach((screen) => {
                let foundScreen = screens.find((s) => s.screenId === screen);
                if (foundScreen) {
                    foundScreen.enabled = true;
                    enabledScreenCount++;
                }
            });

            try {
                response = yield call(
                    gcApi.put,
                    `/device-service/deviceservice/device-info/settings/${setupDetail.advancedMetrics.deviceId}`,
                    {
                        weightOnlyModeEnabled: enabledScreenCount === 0,
                        screenLists: [
                            {
                                screenListId: 'DEFAULT',
                                defaultToLast: true,
                                screens: screens
                            }
                        ]
                    }
                );
            } catch (e) {
                messageType = INITIAL_PREGNANCY_INDEX_SCALE_ERROR;
            }
        }

        if (!setupFailed && setupDetail.trainingStatusEnabled !== null) {
            let date = null;

            if (setupDetail.trainingStatusEnabled) {
                date = moment().format('YYYY-MM-DD');
            }

            try {
                response = yield call(gcApi.put, '/userprofile-service/userprofile/user-settings', {
                    userData: {
                        trainingStatusPausedDate: date
                    }
                });
            } catch (e) {
                messageType = INITIAL_PREGNANCY_TRAINING_STATUS_ERROR;
            }
        }

        yield put({
            type: messageType
        });
    } catch (e) {
        yield put({
            type: INITIAL_PREGNANCY_CREATE_ERROR
        });
    }
}

export function* fetchSnapshots() {
    yield put({
        type: FETCH_PREGNANCY_SNAPSHOTS_FETCHING
    });

    try {
        let response = yield call(gcApi.get, '/periodichealth-service/menstrualcycle/pregnancysnapshot/all');

        yield put({
            type: FETCH_PREGNANCY_SNAPSHOTS_SUCCESS,
            data: response.data
        });
    } catch (e) {
        yield put({
            type: FETCH_PREGNANCY_SNAPSHOTS_FAIL
        });
    }
}

export function* updateSnapshot(action) {
    const { pregnancySettings, originalDueDate, callback } = action;
    const timeStampGmt = DateFormatter.formatConnectTimestampUTC(moment().toDate());

    let newPregnancySettings = {
        ...pregnancySettings,
        reportTimestamp: timeStampGmt // API requires this be included with the payload
    };

    try {
        yield put({
            type: UPDATE_PREGNANCY_SNAPSHOT_UPDATING
        });

        const response = yield call(
            gcApi.put,
            `/periodichealth-service/menstrualcycle/pregnancysnapshot/${originalDueDate}`,
            newPregnancySettings
        );

        yield put({
            type: UPDATE_PREGNANCY_SNAPSHOT_SUCCESS,
            pregnancySnapshot: response.data
        });
        yield call(handlingSuccessCallback, callback, response);
    } catch (e) {
        yield put({
            type: UPDATE_PREGNANCY_SNAPSHOT_FAIL
        });
        yield call(handlingErrorCallback, callback, e);
    }
}

export function* deleteSnapshot(action) {
    const { originalDueDate, callback } = action;

    try {
        let response = yield call(
            gcApi.delete,
            `/periodichealth-service/menstrualcycle/pregnancysnapshot/${originalDueDate}`
        );

        yield call(handlingSuccessCallback, callback, response);
    } catch (e) {
        yield call(handlingErrorCallback, callback, e);
    }
}

export function* fetchDayView(action) {
    let date = action.date;
    const currentDate = new moment().format(DateFormatter.LINK_FORMAT);

    yield put({
        type: FETCH_PREGNANCY_DAYVIEW_FETCHING
    });

    try {
        let response = yield call(
            gcApi.get,
            `/periodichealth-service/menstrualcycle/dayview/${date}?todayDate=${currentDate}`,
            {
                validateStatus: (status) => {
                    return ('' + status).startsWith('20');
                }
            }
        );

        // if we get a 204
        if (!response.data) {
            response.data = {
                dayLog: null
            };
        }

        if (response.data.dayLog === null) {
            response.data.dayLog = {
                calendarDate: date,
                symptoms: null,
                moods: null,
                flow: null,
                discharge: null,
                sexDrive: null,
                sexualActivity: null,
                notes: null,
                reportTimestamp: null,
                ovulationDay: false,
                babyMovements: null,
                hasGlucoseLog: false
            };
        } else if (response.data.dayLog.babyMovements) {
            response.data.dayLog.babyMovements = sortBabyMovements(response.data.dayLog.babyMovements);
        }

        response.data.dayLog.status = response.status;

        yield put({
            type: FETCH_PREGNANCY_DAYVIEW_SUCCESS,
            data: response.data
        });
    } catch (e) {
        yield put({
            type: FETCH_PREGNANCY_DAYVIEW_FAIL
        });
    }
}

export function* updateDailyLog(action) {
    let log = action.log;
    let response = null,
        timeStampGmt = DateFormatter.formatConnectTimestampUTC(moment().toDate());

    if (!log.userProfilePk) {
        log.userProfilePk = ViewerSocialProfile.get('profileId');
    }

    log.reportTimestamp = timeStampGmt;

    if (log.status) {
        delete log.status;
    }

    log = removeNulls(log);

    try {
        response = yield call(gcApi.post, `/periodichealth-service/menstrualcycle/dailylog/${log.calendarDate}`, log);

        if (response && response.data && response.data.babyMovements) {
            response.data.babyMovements = sortBabyMovements(response.data.babyMovements);
        }

        yield put({
            type: UPDATE_PREGNANCY_DAILY_LOG_SUCCESS,
            data: response.data
        });
    } catch (e) {
        yield put({
            type: UPDATE_PREGNANCY_DAILY_LOG_FAIL,
            data: log
        });
    }
}

export function* fetchGlucose(action) {
    let date = action.date;

    yield put({
        type: FETCH_PREGNANCY_GLUCOSE_FETCHING
    });

    try {
        let response = yield call(gcApi.get, `/biometric-service/biometric/glucose/${date}`);

        if (response.data && response.data.glucoseList) {
            response.data.glucoseList = sortGlucoseList(response.data.glucoseList);

            response.data.glucoseList.forEach((item) => {
                if (!item.originalUserMeasurementTimestamp) {
                    item.originalUserMeasurementTimestamp = item.userMeasurementTimestamp;
                }
            });
        }

        yield put({
            type: FETCH_PREGNANCY_GLUCOSE_SUCCESS,
            data: response.data
        });
    } catch (e) {
        yield put({
            type: FETCH_PREGNANCY_GLUCOSE_FAIL
        });
    }
}

export function* addGlucoseLog(action) {
    let timeStampGmt = DateFormatter.formatConnectTimestampUTC(moment().toDate()),
        log = action.message.log,
        reload = action.message.reload;

    delete log.gmtOffset;
    delete log.calendarDate;
    delete log.originalUserMeasurementTimestamp;

    log.reportTimestamp = timeStampGmt;
    log.requestOrigin = 'PREGNANCY';

    try {
        let response = yield call(gcApi.post, '/biometric-service/biometric/glucose', log);

        yield put({
            type: ADD_PREGNANCY_GLUCOSE_SUCCESS
        });

        if (reload) {
            try {
                let date = moment(log.userMeasurementTimestamp).format(DateFormatter.LINK_FORMAT);
                // trigger event to retrieve the updated glucose
                yield put({ ...action, type: FETCH_PREGNANCY_GLUCOSE, date: date });
            } catch (e) {}
        }
    } catch (e) {
        yield put({
            type: ADD_PREGNANCY_GLUCOSE_FAIL
        });
    }
}

export function* updateGlucoseLog(action) {
    let log = action.message.log,
        reload = action.message.reload;

    try {
        delete log.gmtMeasurementTimestamp;

        let response = yield call(gcApi.put, '/biometric-service/biometric/glucose', log);

        yield put({
            type: UPDATE_PREGNANCY_GLUCOSE_SUCCESS
        });

        if (reload) {
            try {
                let date = moment(log.userMeasurementTimestamp).format(DateFormatter.LINK_FORMAT);
                // trigger event to retrieve the updated glucose
                yield put({ ...action, type: FETCH_PREGNANCY_GLUCOSE, date: date });
            } catch (e) {}
        }
    } catch (e) {
        yield put({
            type: UPDATE_PREGNANCY_GLUCOSE_FAIL
        });
    }
}

export function* deleteGlucoseLog(action) {
    let log = action.message.log,
        reload = action.message.reload;

    try {
        let response = yield call(gcApi.delete, `/biometric-service/biometric/glucose/${log.userMeasurementTimestamp}`);

        yield put({
            type: DELETE_PREGNANCY_GLUCOSE_SUCCESS
        });

        if (reload) {
            try {
                let date = moment(log.userMeasurementTimestamp).format(DateFormatter.LINK_FORMAT);
                // trigger event to retrieve the updated glucose
                yield put({ ...action, type: FETCH_PREGNANCY_GLUCOSE, date: date });
            } catch (e) {}
        }
    } catch (e) {
        yield put({
            type: DELETE_PREGNANCY_GLUCOSE_FAIL
        });
    }
}

export function* fetchPregnancyIcon(action) {
    let iconKey = action.message.iconKey;
    let url = PregnancyIcons[iconKey].url;

    try {
        let response = yield call(gcApi.get, url, {
            baseURL: '/'
        });

        yield put({
            type: FETCH_PREGNANCY_ICON_SUCCESS,
            data: {
                iconKey,
                iconData: response.data
            }
        });
    } catch (e) {
        yield put({
            type: FETCH_PREGNANCY_ICON_FAIL
        });
    }
}

export function* fetchPregnancyWeightGainGoals(action) {
    const { startDate, endDate } = action.date;

    yield put({
        type: FETCH_PREGNANCY_WEIGHT_GAIN_GOALS_FETCHING
    });

    try {
        let response = yield call(
            gcApi.get,
            `/periodichealth-service/menstrualcycle/pregnancy/weightgoals/${startDate}/${endDate}`
        );

        yield put({
            type: FETCH_PREGNANCY_WEIGHT_GAIN_GOALS_SUCCESS,
            data: response.data
        });
    } catch (e) {
        yield put({
            type: FETCH_PREGNANCY_WEIGHT_GAIN_GOALS_FAIL
        });
    }
}

// watcher
export const pregnancyTrackingSagas = [
    takeEvery(COMPLETE_STOP_TRACKING, stopTrackingPregnancy),
    takeEvery(TAKE_A_BREAK, takeBreak),
    takeEvery(INITIAL_PREGNANCY_CREATE, create),
    takeEvery(FETCH_PREGNANCY_SNAPSHOTS, fetchSnapshots),
    takeEvery(UPDATE_PREGNANCY_SNAPSHOT, updateSnapshot),
    takeEvery(DELETE_PREGNANCY_SNAPSHOT, deleteSnapshot),
    takeEvery(FETCH_PREGNANCY_DAYVIEW, fetchDayView),
    takeEvery(UPDATE_PREGNANCY_DAILY_LOG, updateDailyLog),
    takeEvery(FETCH_PREGNANCY_GLUCOSE, fetchGlucose),
    takeEvery(ADD_PREGNANCY_GLUCOSE, addGlucoseLog),
    takeEvery(UPDATE_PREGNANCY_GLUCOSE, updateGlucoseLog),
    takeEvery(DELETE_PREGNANCY_GLUCOSE, deleteGlucoseLog),
    takeEvery(FETCH_PREGNANCY_ICON, fetchPregnancyIcon),
    takeEvery(FETCH_PREGNANCY_WEIGHT_GAIN_GOALS, fetchPregnancyWeightGainGoals)
];
